import { Avatar, Card, Tabs } from 'antd';
import clsx from 'clsx';
import { includes, omit } from 'lodash';
import React, { useEffect } from 'react';
import { useMedia } from 'react-use';
import Error404 from '../../Error404';
import useLazyQueryWithCallbacks from '../../common/CommonUseLazyQuery';
import {
  ACCESS_TYPE,
  BREAKPOINTS,
  ROUTES,
  TAB_KEYS,
} from '../../common/constants';
import useRouter from '../../common/useRouter';
import HasAccess from '../../components/HasAccess';
import RegisterTodoList from './components/RegisterTodoList';
import TodoList from './components/TodoList';
import {
  GET_USER_EQC_APPROVAL_LOGS_COUNT,
  GET_USER_REGISTER_APPROVAL_LOGS_COUNT,
  USER_INSTRUCTION_TODO_LIST,
} from './graphql/Queries';
import IssuesList from './issues/IssuesList';

const initialTodoFilter = {
  skip: 0,
  limit: 10,
  sortBy: { field: 'updatedAt', order: 'DESC' },
  isRfi: false,
};
const initialProjectFilter = {
  skip: 0,
  limit: 10,
  sortBy: { field: 'createdAt', order: 'DESC' },
};

const Todo = () => {
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const {
    navigate,
    params: { tab },
  } = useRouter();
  const hasInspectionAccess = !!HasAccess({ type: ACCESS_TYPE.INSPECTION });
  const hasInstructionAccess = !!HasAccess({ type: ACCESS_TYPE.INSTRUCTION });
  const hasRegisterAccess = !!HasAccess({ type: ACCESS_TYPE.REGISTER });

  const getDefaultTodoRoute = () => {
    if (hasInspectionAccess) return ROUTES.EQC;
    if (hasRegisterAccess) return ROUTES.REGISTER;
    if (hasInstructionAccess) return ROUTES.ISSUES;
    return null;
  };

  useEffect(() => {
    const fallbackRoute = getDefaultTodoRoute();
    if (!fallbackRoute) {
      return;
    }
    if (tab === TAB_KEYS.EQC && !hasInspectionAccess) {
      navigate(`${ROUTES.TODO}${fallbackRoute}`);
    }
    if (tab === TAB_KEYS.REGISTER && !hasRegisterAccess) {
      navigate(`${ROUTES.TODO}${fallbackRoute}`);
    }
    if (tab === TAB_KEYS.INSTRUCTION && !hasInstructionAccess) {
      navigate(`${ROUTES.TODO}${fallbackRoute}`);
    }
  }, []);

  const [getUserEqcApprovalLogs, { data }] = useLazyQueryWithCallbacks(
    GET_USER_EQC_APPROVAL_LOGS_COUNT,
    {
      variables: { filter: initialTodoFilter },
      fetchPolicy: 'network-only',
      onError: () => {},
    },
  );
  const [userInstructionTodoList, { data: instructionData }] = useLazyQueryWithCallbacks(
    USER_INSTRUCTION_TODO_LIST,
    {
      variables: { filter: initialProjectFilter },
      onError: () => {},
      fetchPolicy: 'network-only',
    },
  );
  const [getUserRegisterApprovalLogs, { data: registerData }] = useLazyQueryWithCallbacks(
    GET_USER_REGISTER_APPROVAL_LOGS_COUNT,
    {
      variables: { filter: omit(initialTodoFilter, 'isRfi') },
      fetchPolicy: 'network-only',
      onError: () => {},
    },
  );

  useEffect(() => {
    if (hasInspectionAccess) {
      getUserEqcApprovalLogs();
    }
    if (hasInstructionAccess) {
      userInstructionTodoList();
    }
    if (hasRegisterAccess) {
      getUserRegisterApprovalLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!includes([TAB_KEYS.EQC, TAB_KEYS.INSTRUCTION, TAB_KEYS.REGISTER], tab)) {
    return <Error404 />;
  }

  const items = [
    ...(hasInspectionAccess
      ? [
          {
            key: TAB_KEYS.EQC,
            label: (
              <div className="d-flex align-center" id="approvals-tab-label">
                Inspection Approvals
                {data?.getUserEqcApprovalLogs?.total > 0 && (
                  <Avatar
                    className="todo-count"
                    size={25}
                    id="approvals-count-badge"
                  >
                    {data?.getUserEqcApprovalLogs?.total}
                  </Avatar>
                )}
              </div>
            ),
            children: (
              <Card id="approvals-tab-content">
                <div
                  className="header d-flex align-center justify-between mb-10"
                  id="approvals-header"
                >
                  {isDesktopViewport && (
                    <h1 id="approvals-title">Pending Approvals</h1>
                  )}
                  <div
                    id="approvals-header-buttons"
                    className={clsx(
                      'users-header-buttons d-flex',
                      !isDesktopViewport && 'width-percent-100',
                    )}
                  >
                    <div
                      id="search-component"
                      className={clsx(
                        'mr-5',
                        !isDesktopViewport && 'flex-grow-1',
                      )}
                    />
                    <div id="add-button" />
                    <div id="user-import-export-buttons" className="d-flex" />
                  </div>
                </div>
                <TodoList />
              </Card>
            ),
          },
        ]
      : []),
    ...(hasInstructionAccess
      ? [
          {
            key: TAB_KEYS.INSTRUCTION,
            label: (
              <div className="d-flex align-center" id="respond-tab-label">
                To Be Respond
                {instructionData?.userInstructionTodoList?.total > 0 && (
                  <Avatar
                    className="todo-count"
                    size={25}
                    id="respond-count-badge"
                  >
                    {instructionData?.userInstructionTodoList?.total}
                  </Avatar>
                )}
              </div>
            ),
            children: (
              <Card id="respond-tab-content">
                <div
                  className="project-header d-flex justify-between"
                  id="issues-header"
                >
                  {isDesktopViewport && <h1 id="issues-title">Issues</h1>}
                  <div
                    id="issues-header-buttons"
                    className={clsx(
                      'users-header-buttons d-flex',
                      !isDesktopViewport && 'width-percent-100',
                    )}
                  >
                    <div
                      id="search-component"
                      className={clsx(
                        'mr-5',
                        !isDesktopViewport && 'flex-grow-1',
                        'width-percent-100',
                      )}
                    />
                    <div id="add-button" />
                    <div id="user-import-export-buttons" className="d-flex" />
                  </div>
                </div>
                <IssuesList />
              </Card>
            ),
          },
        ]
      : []),
    ...(hasRegisterAccess
      ? [
          {
            key: TAB_KEYS.REGISTER,
            label: (
              <div
                className="d-flex align-center"
                id="register-approvals-tab-label"
              >
                Register Logs Approval
                {registerData?.getUserRegisterApprovalLogs?.total > 0 && (
                  <Avatar
                    className="todo-count"
                    size={25}
                    id="register-approvals-count-badge"
                  >
                    {registerData?.getUserRegisterApprovalLogs?.total}
                  </Avatar>
                )}
              </div>
            ),
            children: (
              <Card id="register-approvals-tab-content">
                <div
                  className="header d-flex align-center justify-between mb-10"
                  id="register-approvals-header"
                >
                  {isDesktopViewport && (
                    <h1 id="register-approvals-title">
                      Pending Register Approvals
                    </h1>
                  )}
                  <div
                    id="register-approvals-header-buttons"
                    className={clsx(
                      'users-header-buttons d-flex',
                      !isDesktopViewport && 'width-percent-100',
                    )}
                  >
                    <div
                      id="search-component"
                      className={clsx(
                        'mr-5',
                        !isDesktopViewport && 'flex-grow-1',
                      )}
                    />
                    <div id="add-button" />
                    <div id="user-import-export-buttons" className="d-flex" />
                  </div>
                </div>
                <RegisterTodoList />
              </Card>
            ),
          },
        ]
      : []),
  ];
  return (
    <>
      {!isDesktopViewport && (
        <>
          <div className="page-header text-primary" id="mobile-todo-header">
            Pending Approvals
          </div>
        </>
      )}
      <div className="mobile-card-wrapper" id="todo-wrapper">
        <div className="project-details setup-module" id="todo-container">
          <div className="details-content" id="todo-content">
            <Tabs
              id="todo-tabs"
              onTabClick={(key) => navigate(`${ROUTES.TODO}/${key}`)}
              activeKey={tab}
              destroyOnHidden
              items={items}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Todo;
